import { z } from 'zod';
export const roles = ['viewer', 'member', 'operator', 'reviewer', 'admin'];
export const roleRank = {
    viewer: 0,
    member: 1,
    operator: 2,
    reviewer: 3,
    admin: 4,
};
export function roleAtLeast(actual, required) {
    return roleRank[actual] >= roleRank[required];
}
export const machineStates = [
    'computing',
    'serving',
    'reserved',
    'maintenance',
    'ready',
    'incident',
    'offline',
    'unknown',
];
export const auditCategories = [
    'workload',
    'reservation',
    'maintenance',
    'diagnostic',
    'incident',
    'configuration',
    'service',
    'research',
    'publication',
    'correction',
];
export const visibilityLevels = ['public', 'embargoed', 'internal'];
export const evidenceSources = ['automatic', 'manual', 'imported'];
export const loginSchema = z.object({
    username: z
        .string()
        .trim()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9._@-]+$/, 'Username contains unsupported characters'),
    password: z.string().min(1).max(1024),
});
const finitePercent = z.number().finite().min(0).max(100);
const optionalShortText = z.string().trim().max(160).optional();
const httpUrl = z
    .url()
    .max(1000)
    .refine((value) => ['http:', 'https:'].includes(new URL(value).protocol), {
    message: 'Only HTTP and HTTPS URLs are allowed',
});
export const telemetrySampleSchema = z.object({
    machineId: z.string().trim().min(1).max(80),
    collectorId: z.string().trim().min(1).max(120),
    timestamp: z.iso.datetime({ offset: true }),
    gpuIndex: z.number().int().min(0).max(31),
    gpuUuid: z.string().trim().min(1).max(160),
    gpuName: z.string().trim().min(1).max(160),
    state: z.enum(machineStates),
    gpuUtilizationPct: finitePercent,
    memoryUtilizationPct: finitePercent,
    memoryUsedMiB: z.number().finite().min(0),
    memoryTotalMiB: z.number().finite().positive(),
    temperatureC: z.number().finite().min(-40).max(150),
    powerW: z.number().finite().min(0).max(5000).nullable(),
    powerLimitW: z.number().finite().positive().max(5000).nullable(),
    smClockMHz: z.number().finite().min(0).max(10000).nullable(),
    memoryClockMHz: z.number().finite().min(0).max(20000).nullable(),
    activeProcessCount: z.number().int().min(0).max(100000),
    serviceCount: z.number().int().min(0).max(10000).default(0),
    projectAlias: optionalShortText,
    workloadLabel: optionalShortText,
    reservationLabel: optionalShortText,
    driverVersion: z.string().trim().max(80).optional(),
});
export const telemetryBatchSchema = z.object({
    collectorId: z.string().trim().min(1).max(120),
    sentAt: z.iso.datetime({ offset: true }),
    samples: z.array(telemetrySampleSchema).min(1).max(256),
});
export const evidenceSchema = z.object({
    label: z.string().trim().min(1).max(120),
    source: z.enum(evidenceSources),
    reference: z.string().trim().max(500).optional(),
    checksum: z.string().trim().max(160).optional(),
});
export const auditDraftInputSchema = z
    .object({
    category: z.enum(auditCategories),
    title: z.string().trim().min(3).max(180),
    summary: z.string().trim().min(10).max(600),
    details: z.string().trim().max(5000).optional(),
    occurredAt: z.iso.datetime({ offset: true }),
    visibility: z.enum(visibilityLevels).default('public'),
    embargoUntil: z.iso.datetime({ offset: true }).nullable().optional(),
    source: z.enum(evidenceSources).default('manual'),
    stateBefore: z.enum(machineStates).nullable().optional(),
    stateAfter: z.enum(machineStates).nullable().optional(),
    projectAlias: z.string().trim().max(80).optional(),
    evidence: z.array(evidenceSchema).max(20).default([]),
    correctionOf: z.string().trim().max(80).nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.visibility === 'embargoed' && !value.embargoUntil) {
        ctx.addIssue({ code: 'custom', path: ['embargoUntil'], message: 'Embargo end is required' });
    }
    if (value.category === 'correction' && !value.correctionOf) {
        ctx.addIssue({ code: 'custom', path: ['correctionOf'], message: 'Correction target is required' });
    }
    if (value.category !== 'correction' && value.correctionOf) {
        ctx.addIssue({
            code: 'custom',
            path: ['correctionOf'],
            message: 'Only corrections may link a target',
        });
    }
});
export const workloadInputSchema = z
    .object({
    title: z.string().trim().min(3).max(180),
    projectAlias: z.string().trim().min(1).max(80),
    publicSummary: z.string().trim().max(600).optional(),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    status: z.enum(['planned', 'queued', 'running', 'completed', 'cancelled']).default('planned'),
    visibility: z.enum(visibilityLevels).default('internal'),
    gpuCount: z.number().int().min(1).max(32).default(1),
})
    .refine((value) => new Date(value.endsAt) > new Date(value.startsAt), {
    path: ['endsAt'],
    message: 'End must be after start',
});
export const incidentInputSchema = z.object({
    title: z.string().trim().min(3).max(180),
    severity: z.enum(['low', 'medium', 'high', 'critical']),
    status: z.enum(['detected', 'acknowledged', 'investigating', 'mitigated', 'resolved', 'reviewed']),
    detectedAt: z.iso.datetime({ offset: true }),
    resolvedAt: z.iso.datetime({ offset: true }).nullable().optional(),
    publicSummary: z.string().trim().max(800).optional(),
    internalNotes: z.string().trim().max(10000).optional(),
});
export const maintenanceInputSchema = z
    .object({
    title: z.string().trim().min(3).max(180),
    scope: z.string().trim().min(3).max(1000),
    status: z.enum(['planned', 'in_progress', 'completed', 'cancelled']),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    result: z.string().trim().max(3000).optional(),
    nextDueAt: z.iso.datetime({ offset: true }).nullable().optional(),
    publicSummary: z.string().trim().max(800).optional(),
    internalNotes: z.string().trim().max(10000).optional(),
})
    .refine((value) => !value.endsAt || new Date(value.endsAt) >= new Date(value.startsAt), {
    path: ['endsAt'],
    message: 'End cannot precede start',
});
export const projectInputSchema = z.object({
    alias: z
        .string()
        .trim()
        .min(2)
        .max(80)
        .regex(/^[A-Za-z0-9_-]+$/),
    title: z.string().trim().min(3).max(200),
    summary: z.string().trim().min(10).max(1200),
    category: z.string().trim().min(2).max(100),
    status: z.enum(['planned', 'active', 'completed', 'archived']).default('active'),
    public: z.boolean().default(true),
});
export const serviceInputSchema = z.object({
    slug: z
        .string()
        .trim()
        .min(2)
        .max(80)
        .regex(/^[a-z0-9-]+$/),
    name: z.string().trim().min(3).max(160),
    publicDescription: z.string().trim().min(10).max(1000),
    status: z.enum(['operational', 'degraded', 'maintenance', 'offline', 'unknown']).default('unknown'),
    statusMessage: z.string().trim().max(300).optional(),
    public: z.boolean().default(true),
    launchUrl: httpUrl.optional(),
    healthUrl: httpUrl.optional(),
    requiredGroups: z.array(z.string().trim().min(1).max(300)).max(30).default([]),
    owner: z.string().trim().max(160).optional(),
});
export const researchInputSchema = z.object({
    title: z.string().trim().min(3).max(300),
    type: z.enum(['paper', 'thesis', 'model', 'dataset', 'software', 'demonstration']),
    authors: z.array(z.string().trim().min(1).max(160)).min(1).max(100),
    venue: z.string().trim().max(240).optional(),
    publishedAt: z.iso.datetime({ offset: true }).nullable().optional(),
    url: httpUrl.optional(),
    doi: z.string().trim().max(200).optional(),
    summary: z.string().trim().min(10).max(1600),
    projectAlias: z.string().trim().max(80).optional(),
    usageStart: z.iso.datetime({ offset: true }).nullable().optional(),
    usageEnd: z.iso.datetime({ offset: true }).nullable().optional(),
    featured: z.boolean().default(false),
    public: z.boolean().default(true),
});
//# sourceMappingURL=index.js.map